import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {BehaviorSubject, first, forkJoin, Observable, of, switchMap} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {catchError, map} from 'rxjs/operators';
import {UserData} from '../../../data/models/user-data.model';
import {HupSubscriptionsService} from '../../../data/services/hup-subscriptions.service';
import {BaseDataService} from '../../services/base-data.service';
import {ComplaintService} from '../../../data/services/complaint.service';
import {MoveService} from '../../../data/services/move.service';
import {PaymentService} from '../../../data/services/payment.service';
import {AuthResponseData} from './auth-response-data.model';
import {RedirectService} from "../../../data/services/redirect.service";
import {isPlatformBrowser, isPlatformServer} from "@angular/common";
import {LandingPageService} from "../../../data/services/landing-page.service";
import {ajax} from "rxjs/ajax";
import {LogonStatusData} from "./logon-status-data.model";
import {ConfigService} from "../../services/config.service";
import {SsoConfiguration} from "./sso-configuration";
import {OtlcData} from "./otlc-data";
import {CookieService} from "ngx-cookie-service";
import {GuestAccountService} from "../../services/guest-account.service";

export interface PasswordComplexityInfo {
  minLength: string;
  lowerChars: string;
  upperChars: string;
  digits: string;
  specialChars: string;
}

@Injectable({providedIn: 'root'})
export class LoginService {
  private readonly http = inject(HttpClient);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly baseDataService = inject(BaseDataService);
  private readonly complaintService = inject(ComplaintService);
  private readonly hupSubscriptionsService = inject(HupSubscriptionsService);
  private readonly moveService = inject(MoveService);
  private readonly paymentService = inject(PaymentService);
  private readonly cookieService = inject(CookieService);
  private readonly redirectService = inject(RedirectService);
  private readonly landingPageService = inject(LandingPageService);
  private readonly configService = inject(ConfigService);
  private readonly guestAccountService = inject(GuestAccountService);
  private readonly platformId = inject<object>(PLATFORM_ID);

  private static readonly COOKIE_NAME: string  = 'webabo-auth-cookie';
  // Fester Pfad noetig: ohne explizites path uebernimmt ngx-cookie-service den Default-Path
  // der aktuellen Route (RFC 6265) -> set()/delete() auf unterschiedlichen Routen erzeugen/loeschen
  // sonst verschiedene Cookie-Instanzen desselben Namens statt einer einzigen.
  private static readonly COOKIE_PATH: string = '/';

  userData: UserData;
  isChildUser$ = new BehaviorSubject<boolean>(null);
  private authData = new BehaviorSubject<AuthResponseData>(null);
  private tokenExpirationTimer: any;
  private isStatusCallRunning = false;

  public getSsoConfiguration(): SsoConfiguration {
    console.log('SSO-Host is ' + this.configService.getConfig('sso.server.host')?.value);
    return {
          usage: this.configService.getConfig('sso.usage')?.value,
          clientId: this.configService.getConfig('sso.client.webabo.id')?.value,
          returnUrl: this.configService.getConfig('sso.client.webabo.returnUrl')?.value,
          ssoHost: this.configService.getConfig('sso.server.host')?.value
    }
  }

  checkStatus() {
    if (isPlatformBrowser(this.platformId)) {
      const ssoConfigKeys = ['sso.usage', 'sso.client.webabo.id', 'sso.client.webabo.returnUrl', 'sso.server.host'];

      forkJoin(ssoConfigKeys.map(key => this.configService.loadConfig(key))).pipe(
        switchMap(() => this.getAuthData().pipe(first()))
      ).subscribe((authData) => {
        const isAuthenticated = !!authData && (authData.token !== null);

        // if child-user state is unknown we need to force status check to retrieve this information
        let childUserState = this.isChildUser$.getValue();

        if (!isAuthenticated || childUserState === null) {
          const ssoConf = this.getSsoConfiguration();
          if (ssoConf.usage?.toUpperCase().startsWith('OAUTHHASSO') && ssoConf.ssoHost) {
            const url = ssoConf.ssoHost + '/auth/status'
              + '?client_id=' + ssoConf.clientId
              + '&redirect_uri=' + encodeURI(ssoConf.returnUrl);

            // Ajax-Call nur durchführen, wenn der nicht schon gerade läuft!
            if (!this.isStatusCallRunning) {
              this.isStatusCallRunning = true;
              const logonStatus = ajax({
                url: url,
                method: 'GET',
                withCredentials: true
              }).pipe(
                map(status => LogonStatusData.of(status.response)),
                catchError(error => {
                  console.error('logon-status-error: ', error);
                  return of(null);
                })
              );

              logonStatus.pipe(first()).subscribe(
                logonStatusData => {
                  if (logonStatusData) {
                    const code = logonStatusData.code;
                    this.isChildUser$.next(!!logonStatusData.parentUserUuid);
                    if (code != null) {
                      this.loginByCode(code).subscribe();
                    }
                  }
                  this.isStatusCallRunning = false;
                },
                () => {
                  this.isStatusCallRunning = false;
                },
                () => {
                  this.isStatusCallRunning = false;
                }
              );
            }
          }
        }
      });
    }
  }

  login(email: string, password: string, route?: string): Observable<any> {
    return this.http.post<AuthResponseData>('/webabo/auth/login', {
        username: email,
        password
      }, {headers: {'Content-Type': 'application/json'}}
    ).pipe(map(authResponseData => {
      if (authResponseData.token?.length) {
        authResponseData.expiration_date = new Date(new Date().getTime() + (authResponseData.timeToLive * 1000));
        this.authData.next(authResponseData);
        this.cookieService.set(LoginService.COOKIE_NAME, JSON.stringify(authResponseData), {expires: authResponseData.expiration_date, path: LoginService.COOKIE_PATH});
        this.autoLogout(authResponseData.expiration_date);
        this.autoRefresh((authResponseData.timeToLive - 10) * 1000);
        if (route) {
          this.redirectService.redirect(route);
        }
      }
      return authResponseData;
    }));
  }

  loginByCode(code: string, route?: string): Observable<any> {
    return this.http.post<AuthResponseData>('/webabo/auth/loginByCode?code=' + code, null).pipe(map(authResponseData => {
      if (authResponseData.token !== '0') {
        authResponseData.expiration_date = new Date(new Date().getTime() + (authResponseData.timeToLive * 1000));
        this.authData.next(authResponseData);
        this.cookieService.set(LoginService.COOKIE_NAME, JSON.stringify(authResponseData), {expires: authResponseData.expiration_date, path: LoginService.COOKIE_PATH});
        this.autoLogout(authResponseData.expiration_date);
        this.autoRefresh((authResponseData.timeToLive - 10) * 1000);
        if (route) {
          this.redirectService.redirect(route);
        }
      } else {
        return authResponseData;
      }
    }));
  }

  loginByOtlc(code: string): void {
    const ssoConf = this.getSsoConfiguration();
      if (ssoConf.usage?.toUpperCase().startsWith('OAUTHHASSO')) {
        const url = ssoConf.ssoHost + '/auth/otlc?code=' + code;
        const otlcResponse = ajax({
          url: url,
          method: 'GET',
          withCredentials: true
        }).pipe(
          map(otlc => OtlcData.of(otlc.response).userId),
          catchError(error => {
            console.error('otlc-error: ', error);
            return of(null);
          })
        );

        otlcResponse.pipe(first()).subscribe(userId => {
          if (userId != null) {
            this.checkStatus();
          }
        });
      }
  }

  autoLogin(): void {
    const data = this.cookieService.get(LoginService.COOKIE_NAME);
    if (data) {
      const userData: AuthResponseData = AuthResponseData.of(JSON.parse(data));
      if (userData) {
        try {
          const expirationDuration = new Date(userData.expiration_date);
          const durationTime = expirationDuration.getTime();
          const newTime = new Date().getTime();
          if (durationTime > newTime) {
            this.authData.next(userData);
            this.autoLogout(expirationDuration);
            this.autoRefresh((userData.timeToLive - 10) * 1000);
          } else {
            this.logout();
          }
        } catch (error) {
          console.log(error);
        }
      }
    }
  }

  logout(): void {
    // zwischengespeicherte Nutzerdaten löschen
    this.resetData();
    localStorage.clear();
    this.paymentService.resetData();
    this.hupSubscriptionsService.resetData();
    this.moveService.resetData();
    this.complaintService.resetData();
    this.landingPageService.resetData();

    this.cookieService.delete(LoginService.COOKIE_NAME, LoginService.COOKIE_PATH);
    if (this.tokenExpirationTimer) {
      clearTimeout(this.tokenExpirationTimer);
    }
    this.tokenExpirationTimer = null;
    const ssoConf = this.getSsoConfiguration();
      if (ssoConf.usage.toUpperCase().startsWith('OAUTHHASSO')) {
        const logoutUrl = ssoConf.ssoHost + '/auth/logout?client_id=' + ssoConf.clientId;
        ajax({
          url: logoutUrl,
          method: 'GET',
          responseType: 'json',
          withCredentials: true
        }).pipe(
          catchError(error => {
            console.error('logout-error: ', error);
            // ggf. User informieren !?
            return of(null);
          })
        ).subscribe(() => {
          this.router.navigate(['/'])
            .then(() => null); // muss man hier etwas anderes machen ??
        });
      } else {
        this.router.navigate(['/'])
          .then(() => null); // muss man hier etwas anderes machen ??
      }
  }

  autoLogout(timeout: Date): void {
    const time = timeout.getTime() - new Date().getTime();

    if(isPlatformBrowser(this.platformId)) {
      this.tokenExpirationTimer = setTimeout(() => {
        this.logout();
      }, time);
    }
  }

  refresh(): Observable<any> {
    return this.http.post<AuthResponseData>('/webabo/auth/refresh?token=' + this.authData.value.refreshToken, null)
      .pipe(map(authResponseData => {
      if (authResponseData.code !== '') {
        authResponseData.expiration_date = new Date(new Date().getTime() + authResponseData.timeToLive * 1000);
        this.authData.next(authResponseData);
        this.cookieService.set(LoginService.COOKIE_NAME, JSON.stringify(authResponseData), {expires: authResponseData.expiration_date, path: LoginService.COOKIE_PATH});
        this.autoLogout(authResponseData.expiration_date);
        this.autoRefresh((authResponseData.timeToLive - 10) * 1000);
      } else {
        return authResponseData;
      }
    }));
  }

  autoRefresh(timeout: number): void {
    if(isPlatformBrowser(this.platformId)) {
      this.tokenExpirationTimer = setTimeout(() => {
        this.refresh().subscribe();
      }, timeout);
    }
  }

  getUserData(): Observable<UserData> {
    if (this.userData) {
      return of(this.userData);
    }
    return this.http.get<UserData>('/webabo/users').pipe(map(userData => {
      this.baseDataService.handleAddress(userData.address);
      this.baseDataService.handlePerson(userData.person);
      this.userData = userData;
      return this.userData;
    }));
  }

  assignClientNo(clientNo: number): Observable<UserData> {
    if(clientNo > 0) {
      return this.http.post<UserData>('/webabo/users/assign-client-no', {clientNo: clientNo}).pipe(
        map(userData => {
          this.baseDataService.handleAddress(userData.address);
          this.baseDataService.handlePerson(userData.person);
          this.userData = userData;
          return this.userData;
        })
      );
    }
  }

  resetData(): void {
    this.authData.next(null);
    this.isChildUser$.next(null);
    this.userData = null;
    this.guestAccountService.reset();
  }

  getAuthData(): Observable<AuthResponseData> {
    if (!this.authData.getValue()) {
      this.autoLogin();
    }
    return this.authData;
  }

  getPasswordComplexityInfo(): Observable<any> {
    const ssoConfiguration = this.getSsoConfiguration();
    if (isPlatformServer(this.platformId) || ssoConfiguration.usage?.toUpperCase().indexOf('OAUTHHASSO') != 0) {
      return of(null);
    } else {
      return ajax.getJSON(ssoConfiguration.ssoHost + '/util/password-complexity').pipe(
        catchError(() => of(null))
      );
    }
  }}
